// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRoles } from '../services/roleService';
import { useSharedSession } from '../auth/useSharedSession';
import { toUserRole } from '../../helios/services/authService';
import { VALID_ROLES } from '../../helios/types';
import type { UserRole } from '../../helios/types';

export interface RoleOption {
  role: UserRole;
  roleId: string;
  /** Scope types IAM allows this role to be assigned at (`RoleDTO.applicableScopes`,
   *  e.g. ["SITE"] for Operator, ["ORGANIZATION"] for OrgAdmin). */
  scopes: string[];
}

export interface RoleCatalogView {
  /** Canonical roles this tenant's role catalogue actually has an id for —
   *  a role with no matching `RoleDTO` can't be assigned and is left out. */
  roleOptions: RoleOption[];
  isLoading: boolean;
  isError: boolean;
}

const SCOPE_NAME = /[A-Za-z_]+/g;

/** The scope names in a piece of text: letters and underscores, upper-cased. */
function scopeNames(text: string): string[] {
  return (text.match(SCOPE_NAME) ?? []).map(name => name.toUpperCase());
}

/**
 * `RoleDTO.applicableScopes` is a free-form string. IAM sends a JSON-array
 * string today ('["ORGANIZATION","REGION","SITE"]'); the mocks send a comma
 * list ("ORGANIZATION,REGION"). A valid JSON array is read as such. Anything
 * else (a comma list, a bracketed or quoted variant, truncated JSON) falls
 * back to pulling out the scope names, so a change of list syntax cannot
 * silently empty the role pickers. Never throws; warns when a non-empty value
 * has no scope names at all.
 */
export function parseScopes(applicableScopes: string | null): string[] {
  const raw = (applicableScopes ?? '').trim();
  if (!raw) return [];

  if (raw.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item): item is string => typeof item === 'string')
          .flatMap(scopeNames);
      }
    } catch {
      // Not strict JSON: read the names out of it below.
    }
  }

  const names = scopeNames(raw);
  if (names.length === 0) {
    console.warn('[roles] applicableScopes has no scope names:', raw);
  }
  return names;
}

/**
 * Resolves `GET /api/v1/roles` (raw IAM role names) down to the canonical
 * roles this tenant can actually assign, each paired with the `roleId` and
 * valid scopes `POST /role-assignments` needs. Shared by the user detail
 * page's Organization Role picker (ADO #22697) and the Invite User form (ADO
 * #22696) so both read the same tenant catalogue the same way, rather than
 * each re-deriving it.
 */
export function useRoleCatalog(): RoleCatalogView {
  const { organizationId, isLoading: sessionLoading } = useSharedSession();

  // Don't ask IAM for roles before the org is known (ADO #33870) — the Invite
  // modal is mounted for the lifetime of the Users page, so without this gate
  // the query fires on first paint with no `tenant_id`. `enabled` only guards
  // the request; `isLoading` below also has to fold in `sessionLoading`; a
  // signed-in user can legitimately have no `organizationId` once `/me`
  // resolves (`AuthContext`), and without that fold the query stays disabled
  // forever and `isPending` never clears, so the modal would spin forever
  // instead of showing the empty-catalogue warning.
  const rolesQuery = useQuery({
    queryKey: ['admin-center', 'roles', organizationId],
    queryFn: () => getRoles(),
    enabled: !!organizationId,
  });

  const roleOptions = useMemo((): RoleOption[] => {
    const roles = rolesQuery.data ?? [];
    const byRole = new Map<UserRole, { roleId: string; scopes: string[] }>();
    for (const r of roles) {
      const canonical = toUserRole(r.roleName);
      // First match wins when IAM has more than one raw name for a canonical
      // role (e.g. "OrgAdmin" and a legacy "Admin").
      if (canonical && !byRole.has(canonical)) {
        byRole.set(canonical, { roleId: r.roleId, scopes: parseScopes(r.applicableScopes) });
      }
    }
    return VALID_ROLES.filter(r => byRole.has(r)).map(role => {
      const entry = byRole.get(role) as { roleId: string; scopes: string[] };
      return { role, roleId: entry.roleId, scopes: entry.scopes };
    });
  }, [rolesQuery.data]);

  return {
    roleOptions,
    isLoading: sessionLoading || (!!organizationId && rolesQuery.isPending),
    isError: rolesQuery.isError,
  };
}
